/**
 * FRZ.2 — Shared frozen-odds registry.
 *
 * Purpose: give the cart (and any other cross-store consumer) an O(1) way
 * to ask "is this outcome currently marked frozen by the server?"
 *
 * The market stores each own a per-store `oddsLookupTable` (module-scoped
 * in previewLive.js and detailLive.js). Neither is exposed on the Vuex
 * state, so the cart cannot inspect them via rootGetters. Rather than
 * cross-import module internals (fragile, easy circular deps), we keep a
 * single Set here that every market path updates alongside its own
 * per-store bookkeeping, and any consumer reads via `isFrozen`.
 *
 * ## Concurrency & correctness
 *
 * The registry lives outside Vue reactivity — it's a plain Set. Consumers
 * MUST call `isFrozen(uniqueId)` at the moment they need the answer, not
 * memoize it. This is intentional: cart's `socketClearSuspendedOnRecover`
 * runs AFTER the market stores' `UPDATE_ODDS_FROM_SOCKET` in the socket
 * dispatch chain (see socket.module.js), so by the time cart consults the
 * registry, the market stores have already reconciled their state and
 * ours is in lockstep.
 *
 * ## What must call sync()
 *
 *   1. `mapSingleMarketToLookup` (previewLive) — initial fetch / append /
 *      NEW_EVENT_LIVE — every outcome is registered/unregistered based on
 *      its `frozenApplied` field.
 *   2. `mapDetailOddsToLookup` (detailLive) — same, for detail view.
 *   3. `SILENT_PATCH_LIVE_DETAIL` (detailLive) and `_patchEventOdds`
 *      (previewLive) — silent refresh V2 may add or remove the freeze
 *      marker on an existing outcome; the registry must reflect the
 *      new truth.
 *
 * Deliberately NOT called from socket ODDS_CHANGE_LIVE handlers — those
 * handlers preserve `frozenApplied` via a guard (see task 13) and never
 * change the frozen state themselves. Calling sync from there would just
 * be redundant work on the hot path (thousands of updates per second in
 * prime time).
 *
 * ## Uniqueness of uniqueId across stores
 *
 * `unique_id` is a per-outcome identifier stable across stores — the
 * feed guarantees the same outcome carries the same `unique_id` whether
 * it appears in the preview list, the detail view, the scroller, etc.
 * A Set keyed on `String(uniqueId)` is therefore correct even though
 * multiple stores register the same outcome.
 *
 * ## Memory considerations
 *
 * The Set grows with the number of DISTINCT frozen outcomes visible on
 * screen. A typical live view has 30-50 events × few frozen odds per
 * event → dozens of entries. `SET_PREVIEW_LIVE` clears the module-scoped
 * `oddsLookupTable` on each fresh fetch — this file's caller does the
 * equivalent for the registry via `clearAll()` when the market store
 * resets. Detail lookup is scoped per-event and short-lived so no
 * explicit clear is needed there.
 */

const _frozenUids = new Set();

/**
 * Add or remove a uniqueId based on the current frozen state.
 * The caller passes the boolean; we translate to add/delete.
 */
export function sync(uniqueId, isFrozenNow) {
    if (uniqueId == null) return;
    const key = String(uniqueId);
    if (isFrozenNow === true) {
        _frozenUids.add(key);
    } else {
        _frozenUids.delete(key);
    }
}

export function isFrozen(uniqueId) {
    if (uniqueId == null) return false;
    return _frozenUids.has(String(uniqueId));
}

/**
 * Wipe the entire registry. Called from `SET_PREVIEW_LIVE` when the
 * preview list is replaced wholesale — mirrors the reset of
 * `oddsLookupTable` in the same mutation so the registry doesn't leak
 * stale uids from the previous page/sport.
 */
export function clearAll() {
    _frozenUids.clear();
}

// Test hook — do not use in production code.
export function _snapshot() {
    return new Set(_frozenUids);
}
